<template>
  <div class="valuation-page min-h-screen flex flex-col items-center">
    <!-- Loading -->
    <div v-if="isLoading" class="flex-grow flex flex-col items-center justify-center gap-3 text-white">
      <Icon type="circle-notch" class="text-3xl" spinner />
      <div class="text-sm opacity-70">Loading your valuation...</div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex-grow flex items-center justify-center p-6">
      <div class="bg-white rounded-2xl p-6 max-w-[420px] text-center shadow-xl">
        <Icon type="triangle-exclamation" class="text-4xl text-warning mb-3" />
        <div class="text-lg font-semibold mb-1">Something went wrong</div>
        <div class="text-quaternary">{{ error }}</div>
      </div>
    </div>

    <div v-else class="w-full max-w-[560px] flex flex-col flex-grow">
      <!-- Hero -->
      <div class="text-white px-6 pt-8 pb-14 text-center relative">
        <div class="flex justify-center min-h-[40px]">
          <img
            v-if="dealership?.manufacturerLogo"
            :src="dealership.manufacturerLogoPng"
            alt="Manufacturer logo"
            class="max-h-[40px] max-w-[80px] hero-logo mb-4"
            style="filter: brightness(0) invert(1)"
          />
        </div>

        <img
          :src="vehicle?.imageUrl || defaultVehicleImage"
          alt="Your vehicle"
          class="mx-auto max-h-[220px] -my-8 object-contain drop-shadow-2xl"
          @error="onVehicleImageError"
        />

        <div v-if="vehicle?.vrm" class="flex justify-center">
          <LicensePlate :plate="vehicle.vrm" country="uk" class="text-black" />
        </div>

        <div v-if="customerForename" class="mt-6 text-sm font-medium opacity-60">Hi {{ customerForename }},</div>
        <h1 class="text-3xl font-semibold mt-1 leading-tight">
          <template v-if="!hasUploadedImages">Get an accurate valuation for your {{ vehicle.model }}</template>
          <template v-else>Your {{ vehicle.model }} is being valued</template>
        </h1>
        <p v-if="!hasUploadedImages" class="mt-3 text-md opacity-75 max-w-[400px] mx-auto">
          To provide you with the most accurate offers, we need to see your vehicle.
        </p>
      </div>

      <!-- Content card -->
      <div class="bg-white flex-grow rounded-t-3xl -mt-6 relative px-5 py-7 sm:px-8">
        <!-- ============ Not yet uploaded ============ -->
        <template v-if="!hasUploadedImages">
          <!-- Benefits -->
          <div class="grid gap-4 mb-8">
            <div v-for="benefit in benefits" :key="benefit.title" class="flex items-center gap-4">
              <div class="benefit-icon">
                <Icon :type="benefit.icon" fa-style="far" class="text-lg" />
              </div>
              <div>
                <div class="font-semibold text-gray-900">{{ benefit.title }}</div>
                <div class="text-sm text-quaternary">{{ benefit.subtitle }}</div>
              </div>
            </div>
          </div>

          <Button label="Start" block size="lg" class="cta-button" @click="startCapture" />
        </template>

        <!-- ============ Already uploaded ============ -->
        <template v-else>
          <div class="text-xl font-semibold text-gray-900 mb-4">Your submission summary</div>

          <div class="summary-card mb-4">
            <div class="flex items-center gap-4">
              <span class="capture-icon">
                <Icon type="camera" fa-style="far" class="text-sm" />
              </span>
              <div class="flex-grow">
                <div class="font-medium text-gray-900">Photos</div>
                <div class="text-sm text-quaternary">
                  <template v-if="uploadedImageCount">{{ uploadedImageCount }} uploaded</template>
                  <template v-else>Uploaded</template>
                  <template v-if="uploadedAtDisplay"> · {{ uploadedAtDisplay }}</template>
                </div>
              </div>
              <Icon type="circle-check" fa-style="fas" class="text-xl text-success" />
            </div>
            <div class="border-t border-gray-100 my-4" />
            <div class="flex items-center gap-4">
              <span class="capture-icon">
                <Icon type="video" fa-style="far" class="text-sm" />
              </span>
              <div class="flex-grow">
                <div class="font-medium text-gray-900">Walkaround video</div>
                <div class="text-sm text-quaternary">Coming soon</div>
              </div>
              <Icon type="lock" fa-style="far" class="text-lg text-gray-300" />
            </div>
          </div>

          <!-- Valuation status -->
          <div class="status-panel mb-6">
            <div class="status-pulse">
              <Icon type="magnifying-glass-dollar" fa-style="far" class="text-lg" />
            </div>
            <div>
              <div class="font-semibold text-gray-900">Dealership is currently valuing your vehicle</div>
              <div class="text-sm text-quaternary mt-1">
                {{ dealership?.name || 'Your dealership' }} will be in touch as soon as your valuation is ready.
              </div>
            </div>
          </div>
        </template>

        <!-- Footer -->
        <div class="mt-8 flex items-start justify-center gap-2 text-center">
          <Icon type="lock" fa-style="far" class="text-sm text-gray-400 mt-[2px]" />
          <span class="text-sm text-quaternary max-w-[320px]">
            Your information is secure and will only be used to value your vehicle.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'

import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'
import LicensePlate from '@/components/license-plate/LicensePlate.vue'
import { useValuation } from '@/composables/useValuation'
import { useDarkPage } from '@/composables/useDarkPage'

useDarkPage('split')

const route = useRoute()
const router = useRouter()

const valuationId = route.params.vid
const defaultVehicleImage = 'https://cdn.retainauto.com/img/vehicles/default.svg'

const { valuation, vehicle, dealership, customerForename, isLoading, error, hasUploadedImages, uploadedImageCount, load } =
  useValuation(valuationId)

const vehicleName = computed(() => {
  if (!vehicle.value) return 'vehicle'
  return [vehicle.value.make, vehicle.value.model].filter(Boolean).join(' ') || 'vehicle'
})

const uploadedAtDisplay = computed(() => {
  const uploadedAt = valuation.value?.imagesUploadedAt
  if (!uploadedAt) return null
  const parsed = dayjs(uploadedAt)
  return parsed.isValid() ? parsed.format('Do MMM [at] HH:mm') : null
})

const benefits = [
  { icon: 'clock', title: 'Quick & Easy', subtitle: 'Takes just a few minutes' },
  { icon: 'shield-check', title: 'Secure & Private', subtitle: 'Your details and images are safe' },
  { icon: 'tag', title: 'Best Possible Price', subtitle: 'More images = more accurate valuation' }
]

function startCapture() {
  router.push(`/valuation/${valuationId}/image-capture`)
}

function onVehicleImageError(event) {
  event.target.onerror = null
  event.target.src = defaultVehicleImage
}

onMounted(() => {
  load()
})
</script>

<style scoped>
.valuation-page {
  background: radial-gradient(
    120% 100% at 50% 0%,
    var(--manufacturer-gradient-1) 0%,
    var(--manufacturer-gradient-1) 30%,
    var(--manufacturer-gradient-2) 100%
  );
  padding-top: env(safe-area-inset-top);
}

.hero-logo {
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.4));
}

.benefit-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: color-mix(in srgb, var(--manufacturer-color) 10%, white 90%);
  color: var(--manufacturer-color);
}

.capture-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: color-mix(in srgb, var(--manufacturer-color) 8%, white 92%);
  color: var(--manufacturer-color);
}

.summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
}

.status-panel {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  border-radius: 16px;
  padding: 20px;
  background-color: color-mix(in srgb, var(--manufacturer-color) 6%, white 94%);
  border: 1px solid color-mix(in srgb, var(--manufacturer-color) 20%, white 80%);
}

.status-pulse {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: color-mix(in srgb, var(--manufacturer-color) 12%, white 88%);
  color: var(--manufacturer-color);
  animation: status-pulse 2.5s ease-in-out infinite;
}

@keyframes status-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--manufacturer-color) 35%, transparent);
  }
  50% {
    box-shadow: 0 0 0 10px transparent;
  }
}

.cta-button {
  background-color: var(--manufacturer-color) !important;
  padding-top: 12px;
  padding-bottom: 12px;
}
</style>

<style>
/* Fill the whole screen (status bar, home indicator, overscroll) with the dark background */
html.page-dark,
html.page-dark body {
  background: var(--manufacturer-gradient-2, #0b1526);
}

/* Hub page: dark gradient behind the top of the page, white behind the bottom
   (the page ends in a white content card, so the browser's bottom chrome and
   overscroll should be white too) */
html.page-dark-split,
html.page-dark-split body {
  background: linear-gradient(
    to bottom,
    var(--manufacturer-gradient-1, #1a2c50) 0%,
    var(--manufacturer-gradient-2, #0b1526) 50%,
    #ffffff 50%,
    #ffffff 100%
  );
}

/* Override brand colours so shared components pick up the manufacturer accent */
:root {
  --manufacturer-color: #2563eb;
  --manufacturer-gradient-1: #1a2c50;
  --manufacturer-gradient-2: #0b1526;
  --brand-300: color-mix(in srgb, var(--manufacturer-color) 30%, white 70%);
  --brand-600: color-mix(in srgb, var(--manufacturer-color) 60%, white 40%);
  --bg-brand-light: color-mix(in srgb, var(--manufacturer-color) 8%, white 92%);
  --bg-brand-solid: var(--manufacturer-color);
  --fg-brand: var(--manufacturer-color);
  --border-brand: color-mix(in srgb, var(--manufacturer-color) 30%, white 70%);
}
</style>
